import React, { useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import styled from "@emotion/styled"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Button from "src/components/Button"
import PostCard from "src/components/PostCard"
import Slider from "react-slick"
import { mq, globals } from "src/styles"

const Wrapper = styled(Section)`
  text-align: center;
  // overflow: hidden;
  // width: 100%;
  // background: #ccc;
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const Slide = styled.div`
  padding: 10px;
  height: 100%;
  ${mq.mediumAndBelow} {
    padding: 10px 0 10px 20px;
  }
  > .post-card {
    width: calc((100vw - 20px * 5 - (100vw / 7)) / 4);
    ${mq.largerAndBelow} {
      width: calc((100vw - 20px * 4 - (100vw / 7)) / 3);
    }
    ${mq.largeAndBelow} {
      width: 40vw;
    }
    ${mq.mediumAndBelow} {
      width: 40vw;
    }
    ${mq.smallAndBelow} {
      width: 80vw;
    }
  }
  ${({ firstSlide }) =>
    firstSlide
      ? `
    padding-left: var(--site-margins);
    ${mq.mediumAndUp} {
      padding-left: ${100 / 14}vw;
    }
    ${mq.largerAndUp} {
      padding-left: calc(${100 / 14}vw + 15px);
    }
  `
      : ""}
  ${({ lastSlide }) =>
    lastSlide
      ? `
    padding-right: var(--site-margins);
    ${mq.mediumAndUp} {
      padding-right: ${100 / 14}vw;
    }
    ${mq.largerAndUp} {
      padding-right: calc(${100 / 14}vw + 15px);
    }
  `
      : ""}
`

const PostSlider = styled(Slider)`
  width: 100%;
  max-width: 100%;
  .slick-track {
    display: flex;
    align-items: stretch;
    .slick-slide {
      float: none !important;
      height: auto;
      flex-shrink: 0;
      flex-grow: 0;
      > div {
        height: 100%;
        .post-card {
          height: 100%;
        }
      }
    }
  }
  > div {
    > a {
      height: 100%;
    }
  }
`

const SectionHeader = styled.div`
  margin-top: -20px;
  ${globals.verticalSpacing("padding-bottom", 0.3)}
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  ${({ postCount }) => `
    ${postCount > 4 ? "display: block;" : "display: none;"}
    ${mq.largerAndBelow} {
      ${postCount > 3 ? "display: block;" : "display: none;"}
    }
    ${mq.largeAndBelow} {
      ${postCount > 2 ? "display: block;" : "display: none;"}
    }
    ${mq.mediumAndBelow} {
      ${postCount > 2 ? "display: block;" : "display: none;"}
    }
    ${mq.smallAndBelow} {
      ${postCount > 1 ? "display: block;" : "display: none;"}
    }
  `}
  > button:first-of-type {
    margin-right: 10px;
  }
`

const ArticleSlider = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headingSize,
  headline,
  listingType,
  posts,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const { recentPosts, pressPosts } = useStaticQuery(
    graphql`
      query {
        recentPosts: allSanityPost(
          sort: { fields: publishedAt, order: DESC }
          limit: 10
        ) {
          edges {
            node {
              ...Post
            }
          }
        }
        pressPosts: allSanityPost(
          sort: { fields: publishedAt, order: DESC }
          filter: { categories: { elemMatch: { title: { eq: "Press" } } } }
        ) {
          edges {
            node {
              ...Post
            }
          }
        }
      }
    `
  )

  if (listingType === "press") {
    posts = pressPosts?.edges?.slice(0, 10)
  } else if (listingType === "most-recent") {
    posts = recentPosts?.edges
  }

  const slider = useRef(null)
  const settings = {
    arrows: false,
    slidesToShow: posts.length <= 4 ? posts.length : 4,
    infinite: false,
    variableWidth: true,
    centerMode: false,
    swipe: true,
    speed: 500,
    responsive: [
      {
        breakpoint: mq.largerBreakpoint,
        settings: {
          slidesToShow: posts.length <= 3 ? posts.length : 3,
        },
      },
      {
        breakpoint: mq.largeBreakpoint,
        settings: {
          slidesToShow: posts.length <= 2 ? posts.length : 2,
        },
      },
      {
        breakpoint: mq.mediumBreakpoint,
        settings: {
          slidesToShow: posts.length <= 2 ? posts.length : 2,
        },
      },
      {
        breakpoint: mq.smallBreakpoint,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      fullWidth
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Grid small="m [1] m" medium="1 [12] 1" large="m 1 [12] 1 m">
        <SectionHeader>
          {headline ? (
            <h3 className={headingSize ? headingSize : "h3"}>{headline}</h3>
          ) : null}
          <SliderControls postCount={posts?.length}>
            <Button
              icon="arrow_back"
              onClick={() => slider?.current?.slickPrev()}
              shape="circle"
              title="Go to previous slide"
              setTheme="white"
            />
            <Button
              icon="arrow_forward"
              onClick={() => slider?.current?.slickNext()}
              shape="circle"
              title="Go to next slide"
              setTheme="white"
            />
          </SliderControls>
        </SectionHeader>
      </Grid>
      <PostSlider ref={slider} {...settings}>
        {posts.map((post, index) => (
          <Slide
            key={post._key}
            firstSlide={index === 0}
            lastSlide={index === posts.length - 1}
          >
            <PostCard post={post?.node || post} />
          </Slide>
        ))}
      </PostSlider>
    </Wrapper>
  )
}

export default ArticleSlider
